import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import {
    Col,
    Container,
    Row,
    Spinner,
    Alert,
    Card,
    FormGroup,
    InputGroup,
    InputGroupText,
    Input,
    UncontrolledTooltip
} from "reactstrap";
import Accordion from "react-bootstrap/Accordion";
import axios from "axios";
import { AUTH_API_DOMAIN_URL, EMPTY_LIST } from "src/common/constants";
import DOMPurify from 'dompurify';
import Navigator from "src/components/navigator";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getActiveModuleInfos } from "src/helpers/module_helper";
import { useEffect, useState } from "react";

const styles = {
    input: {
        backgroundColor: "#f9f8fa",
        border: "1px solid #fff"
    },
    addon: {
        backgroundColor: "#f9f8fa",
        border: "1px solid #fff"
    },
    inputGroup: {
        height: "48px"
    }
};

const normalize = (str) => {
    return str.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
}

const FAQ = ({
    t,
    lng,
    token
}) => {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [currentActiveAccorion, setCurrentActiveAccorion] = useState(0);

    function createSanitizedMarkup(html) {
        return { __html: DOMPurify.sanitize(html) };
    }

    const {
        data: faqList,
        isFetching: faqIsFetching
    } = useQuery({
        queryKey: [
            "app-fetch-faq-query",
            lng
        ],
        queryFn: async () => {
            const headers = token ? {
                Authorization: `Bearer ${token}`
            } : {};

            const activeModule = getActiveModuleInfos();

            const response = await axios.get(
                `${AUTH_API_DOMAIN_URL}faq/list?module=${activeModule.id}&language=${lng}`,
                {
                    headers
                }
            );

            return response.data?.data?.faqs || [];
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("Failed to load faq list."), {
                type: "error"
            });
        }
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [query]);

    const filteredFaqs = (faqList || []).filter(
        ({ question, answer }) =>
            normalize(question).includes(normalize(debouncedQuery)) ||
            normalize(answer).includes(normalize(debouncedQuery))
    );

    return (
        <div className="page-content faq-page">
            <Container fluid>
                <Row>
                    <Col sm="12" className="mb-3">
                        <Navigator
                            pageTitle={t("FAQ")}
                            breadCrumbs={[
                                { title: "SIP", link: "/" },
                                { title: t("FAQ"), link: "#" },
                            ]}
                        />
                    </Col>

                    <Col sm="12" className="mb-4">
                        <FormGroup className="w-25">
                            <InputGroup style={styles.inputGroup}>
                                <InputGroupText style={styles.addon}>
                                    <i className="fa fa-search" />
                                </InputGroupText>
                                <Input
                                    disabled={faqIsFetching}
                                    type="text"
                                    autoComplete="off"
                                    name="search"
                                    placeholder={`${t("Search FAQs")}...`}
                                    style={styles.input}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                                <InputGroupText
                                    id="search-faq-help"
                                    style={styles.addon}
                                >
                                    <i className="fa fa-info-circle" />
                                </InputGroupText>
                                <UncontrolledTooltip
                                    target="search-faq-help"
                                    placement="top"
                                >
                                    {t('Search within FAQ titles and answers.')}
                                </UncontrolledTooltip>
                            </InputGroup>
                        </FormGroup>
                    </Col>

                    <Col sm="12">
                        {faqIsFetching ? (
                            <p className="text-center">
                                <Spinner
                                    animation="border"
                                    variant="primary"
                                    size="sm"
                                    className="me-2"
                                />
                                {t("Loading")}...
                            </p>
                        ) : (
                            <Accordion
                                defaultActiveKey={0}
                                flush
                                onSelect={(e) => setCurrentActiveAccorion(e)}
                            >
                                {!filteredFaqs?.length && (
                                    <Alert
                                        color="warning"
                                        className="text-center"
                                    >
                                        {t(EMPTY_LIST)}
                                    </Alert>
                                )}

                                {(filteredFaqs || []).map((faq, index) => {
                                    return (
                                        <Card className="mb-3" key={index}>
                                            <Accordion.Item
                                                eventKey={index}
                                            >
                                                <Accordion.Header>
                                                    <i
                                                        className={currentActiveAccorion === index ? 'mdi mdi-chevron-up accor-arrow-icon' : 'mdi mdi-chevron-right accor-arrow-icon'}
                                                    />
                                                    <div
                                                        dangerouslySetInnerHTML={createSanitizedMarkup(faq.question)}
                                                    />
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <div
                                                        dangerouslySetInnerHTML={createSanitizedMarkup(faq.answer)}
                                                    />
                                                </Accordion.Body>

                                            </Accordion.Item>
                                        </Card>
                                    )
                                })}
                            </Accordion>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { user, token } = state.Login;
    const { App, Modules } = state;
    const organization = state.Organization;
    return { user, token, App, organization, modules: Modules };
};

export default connect(mapStatetoProps, {})(withNamespaces()(FAQ));
